import { Injectable } from '@angular/core';
import {
  CompanyContextFieldsFragment,
  CompanyContextGQL,
  IntegrationType,
} from '@graphql/generated/graphql';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  take,
  tap,
} from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CompanyContextService {
  private readonly _state$ =
    new BehaviorSubject<CompanyContextFieldsFragment | null>(null);
  private _load$?: Observable<CompanyContextFieldsFragment>; // Memoized Load-Stream

  readonly company$ = this._state$.asObservable();

  readonly integrationType$: Observable<IntegrationType> = this.company$.pipe(
    map(ctx => ctx?.integrationType ?? null),
    filter((v): v is IntegrationType => v !== null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly studioApp$: Observable<boolean> = this.company$.pipe(
    map(ctx => Boolean(ctx?.studioApp)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  get value(): CompanyContextFieldsFragment | null {
    return this._state$.value;
  }

  constructor(private readonly _companyContextGQL: CompanyContextGQL) {}

  load(): void {
    if (!this._load$) {
      this._load$ = this._fetchCompanyInformation().pipe(
        shareReplay({ bufferSize: 1, refCount: true })
      );
      this._load$.pipe(take(1)).subscribe();
    }
  }

  private _fetchCompanyInformation(): Observable<CompanyContextFieldsFragment> {
    return this._companyContextGQL
      .fetch({}, { fetchPolicy: 'cache-first' })
      .pipe(
        map(res => res.data.company as CompanyContextFieldsFragment),
        tap(ctx => this._state$.next(ctx)),
        catchError(() => {
          this._state$.next(null);
          return of(null as unknown as CompanyContextFieldsFragment);
        }),
        filter((v): v is CompanyContextFieldsFragment => v !== null)
      );
  }
}
